import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BottomPopup from '~/app/common/BottomPopup';
import Button from '~/app/common/Button';
import Icon from '~/app/common/Icon';
import { MaterialIcons as Icons } from '~/app/common/Icon/icons';
import useTranslation from "~/app/hooks/useTranslation";
import MultiSelect from '~/app/modules/form/MultiSelect';
import { useStoreActions, useStoreState } from '~/store';
import classes from './style.module.scss';
import Input from '~/app/modules/form/Search/Input';
import Checkbox from '~/app/modules/form/Checkbox';

export type SearchComponent = React.FC

const Search: SearchComponent = () => {
  const { t } = useTranslation('tasks.listing');
  const { t: tf } = useTranslation('tasks.filters');

  const userRole = useStoreState(state => state.user.currentRole);
  const departments = useStoreState(state => state.tasks.filterOptions.departments);
  const projects = useStoreState(state => state.tasks.filterOptions.projects);
  const fetching = useStoreState(state => state.tasks.fetching);
  const filters = useStoreState(state => state.tasks.filters);
  const currentUserId = useStoreState(state => state.user.data.id);
  const fetchFilterOptions = useStoreActions(actions => actions.tasks.fetchFilterOptions);
  const setFiltersAction = useStoreActions(actions => actions.tasks.setFilters);

  const [open, setOpen] = useState(false);
  const [departmentIds, setDepartmentIds] = useState<string[]>(filters.departments);
  const [projectIds, setProjectIds] = useState<string[]>(filters.projects);
  const [onlyResponsible, setOnlyResponsible] = useState<boolean>(filters.responsible.length === 1);
  const [searchText, setSearchText] = useState<string>(filters.searchText);

  const departmentOptions = useMemo(() => 
    departments && departments.map(dept => ({ text: dept.number + ' - ' + dept.name, value: dept.id.toString() })),
    [departments]
  );

  const projectOptions = useMemo(() =>
    projects && projects.map(project => ({ text: project.number + ' - ' + project.name, value: project.id.toString() })),
    [projects]
  );

  const searchTasks = useCallback(
    (searchTerm: string): void => {
      setSearchText(searchTerm);
      setFiltersAction({ projects: projectIds, departments: departmentIds, responsible: onlyResponsible ? [currentUserId] : [], searchText: searchTerm });
    },
    [onlyResponsible, projectIds, departmentIds, currentUserId, setFiltersAction]
  );

  const applyFilters = () => {
    setFiltersAction({ projects: projectIds, departments: departmentIds, responsible: onlyResponsible ? [currentUserId] : [], searchText: searchText.trim() });
    setOpen(false);
  }

  const clearAllFilters = () => {
    setDepartmentIds([]);
    setProjectIds([]);
    setOnlyResponsible(false);
    setFiltersAction({ projects: [], departments: [], responsible: [], searchText: searchText });
    setOpen(false);
  };

  const handleMultiSelectChange = (key: string, selectedOptions: string[]) => {
    switch (key) {
      case 'department':
        setDepartmentIds(selectedOptions)
        break;
      case 'project':
        setProjectIds(selectedOptions)
        break;
      default:
        break;
    }
  }

  const checkboxClickHandler = useCallback((): void => {
    setOnlyResponsible(!onlyResponsible);
  }, [onlyResponsible, setOnlyResponsible]);
  
  useEffect(() => {
    if (userRole) {
      fetchFilterOptions(userRole);
    }
  }, [userRole, fetchFilterOptions]);

  return (
    <div className={classes['search-wrap']}>
      <Input
        autoFocus={false}
        placeholder={t("search")}
        className={classes["field"]}
        inputClassName={classes["input"]}
        onSearch={searchTasks}
        fetching={fetching}
        value={filters.searchText}
        iconPosition='left'
      />
      <div className={`${classes['filter-button-wrapper']} ${(filters.departments?.length || filters.projects?.length) ? classes['updated'] : ''}`} onClick={() => setOpen(true)}>
        <Icon
          name={'filter_list' as Icons}
          className={classes['bottomIcon']}
        />
      </div>
      <BottomPopup show={open} onClose={() => setOpen(false)}>
        <h3 className={classes['filter-popup-header']}>{tf('header')}</h3>
        <MultiSelect
          className={classes['input-container']}
          name="departmentId"
          placeholder={tf('department')}
          options={departmentOptions}
          defaultOptions={filters.departments}
          optionChange={(selectedOptions: string[]) => handleMultiSelectChange('department', selectedOptions)}
          icon="keyboard_arrow_down"
        />
        <MultiSelect
          className={classes['input-container']}
          name="projectId"
          placeholder={tf('project')}
          options={projectOptions}
          defaultOptions={filters.projects}
          optionChange={(selectedOptions: string[]) => handleMultiSelectChange('project', selectedOptions)}
          icon="keyboard_arrow_down"
        />
        <Checkbox
          name='checkbox'
          className={classes['checkbox-wrapper']}
          inputClassName={`${classes['checkbox']} ${(onlyResponsible) ? classes['checked'] : ''}`}
          placeholder={tf('assignedToMe')}
          defaultChecked={onlyResponsible}
          checked={onlyResponsible}
          onChange={checkboxClickHandler}
        />
        <div className={classes['btn-container']}>
          <Button className={classes['clear-btn']} onClick={clearAllFilters}>
            {tf('clear')}
          </Button>
          <Button className={classes['apply-btn']} onClick={applyFilters}>
            {tf('apply')}
          </Button>
        </div>
      </BottomPopup>
    </div>
  );
};

export default Search